/**
 * Boot Coordinator Service
 *
 * Coordinates the app boot sequence on native platforms (iOS/Android).
 * Ensures Firebase is fully initialized before the app renders.
 *
 * Boot Phases:
 * 1. 'splash' - Native splash screen visible, JS loading
 * 2. 'boot-screen' - React boot screen with animations, Firebase initializing
 * 3. 'ready' - Firebase ready, app can render
 * 4. 'error' - Boot failed, show error with retry
 */

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { nativeFirebase, UnifiedUser } from './nativeFirebaseService';

// ============================================
// TYPES
// ============================================

export type BootPhase = 'splash' | 'boot-screen' | 'ready' | 'error';

export interface BootState {
  phase: BootPhase;
  firebaseReady: boolean;
  authResolved: boolean;
  user: UnifiedUser | null;
  error: Error | null;
  bootStartTime: number;
  bootDuration: number | null;
}

export type BootStateListener = (state: BootState) => void;

// ============================================
// CONSTANTS
// ============================================

const BOOT_TIMEOUTS = {
  MIN_SPLASH_DURATION: 300,    // Minimum time to show native splash (ms)
  MIN_BOOT_SCREEN_DURATION: 2000, // TODO: REMOVE - temporary minimum to test animation (ms)
  MAX_BOOT_DURATION: 20000,    // Maximum boot time before error (ms)
  FIREBASE_INIT_TIMEOUT: 15000, // Firebase initialization timeout (ms)
  AUTH_RESOLVE_TIMEOUT: 10000, // Auth state resolution timeout (ms)
};

// ============================================
// BOOT COORDINATOR CLASS
// ============================================

class BootCoordinator {
  private state: BootState;
  private listeners: Set<BootStateListener>;
  private bootPromise: Promise<void> | null = null;
  private isNative: boolean;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.listeners = new Set();
    this.state = {
      phase: 'splash',
      firebaseReady: false,
      authResolved: false,
      user: null,
      error: null,
      bootStartTime: Date.now(),
      bootDuration: null,
    };
  }

  /**
   * Get current boot state
   */
  getState(): BootState {
    return { ...this.state };
  }

  /**
   * Subscribe to boot state changes
   */
  subscribe(listener: BootStateListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this.getState());

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const currentState = this.getState();
    this.listeners.forEach(listener => listener(currentState));
  }

  /**
   * Update state and notify listeners
   */
  private updateState(updates: Partial<BootState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  /**
   * Main boot sequence
   * Should be called from index.tsx before rendering the app
   */
  async boot(): Promise<void> {
    // Return existing promise if already booting
    if (this.bootPromise) {
      return this.bootPromise;
    }

    // On web, skip the boot sequence (Firebase is handled differently)
    if (!this.isNative) {
      console.log('BootCoordinator: Web platform, skipping native boot sequence');
      this.updateState({
        phase: 'ready',
        firebaseReady: true,
        authResolved: true,
        bootDuration: 0,
      });
      return;
    }

    this.bootPromise = this.performBoot();
    return this.bootPromise;
  }

  /**
   * Perform the actual boot sequence
   */
  private async performBoot(): Promise<void> {
    const startTime = Date.now();
    console.log('BootCoordinator: Starting native boot sequence...');

    try {
      // Step 1: Ensure minimum splash duration for smooth UX
      const splashElapsed = Date.now() - this.state.bootStartTime;
      if (splashElapsed < BOOT_TIMEOUTS.MIN_SPLASH_DURATION) {
        await this.delay(BOOT_TIMEOUTS.MIN_SPLASH_DURATION - splashElapsed);
      }

      // Step 2: Preload the first poster in the queue.
      // The shuffle order is determined by BootScreen and communicated here via
      // bootPosterPreloadUrl — set before hideSplash so the image is cached when shown.
      const firstPosterUrl = bootPosterPreloadUrl;
      if (firstPosterUrl) {
        await Promise.race([
          new Promise<void>(resolve => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = firstPosterUrl;
          }),
          this.delay(3000),
        ]);
      }

      // Step 3: Hide native splash and show React boot screen
      console.log('BootCoordinator: Hiding native splash, showing boot screen');
      await this.hideSplash();
      this.updateState({ phase: 'boot-screen' });

      // Step 3: Set up overall boot timeout
      const bootTimeoutPromise = this.delay(BOOT_TIMEOUTS.MAX_BOOT_DURATION).then(() => {
        throw new Error('Boot timeout: App took too long to initialize');
      });

      // Step 4: Initialize Firebase and resolve auth (with timeout)
      // Also enforce a minimum boot-screen duration for animation testing (TODO: REMOVE)
      await Promise.race([
        Promise.all([
          this.initializeFirebaseAndAuth(),
          this.delay(BOOT_TIMEOUTS.MIN_BOOT_SCREEN_DURATION),
        ]),
        bootTimeoutPromise,
      ]);

      // Step 5: Boot complete!
      const bootDuration = Date.now() - startTime;
      console.log(`BootCoordinator: Boot complete in ${bootDuration}ms`);

      this.updateState({
        phase: 'ready',
        bootDuration,
      });

    } catch (error) {
      console.warn('BootCoordinator: Boot failed, retrying once...', error);

      try {
        await this.initializeFirebaseAndAuth();
        const bootDuration = Date.now() - startTime;
        console.log(`BootCoordinator: Boot succeeded on auto-retry in ${bootDuration}ms`);
        this.updateState({ phase: 'ready', bootDuration });
      } catch (retryError) {
        console.error('BootCoordinator: Boot failed after auto-retry:', retryError);
        this.updateState({
          phase: 'error',
          error: retryError as Error,
          bootDuration: Date.now() - startTime,
        });
      }
    }
  }

  /**
   * Initialize Firebase and resolve auth state
   */
  private async initializeFirebaseAndAuth(): Promise<void> {
    console.log('BootCoordinator: Initializing Firebase...');

    try {
      // Initialize Firebase + App Check before anything else
      const { firebaseManager } = await import('@watchlist/shared');
      await firebaseManager.initialize();
      console.log('BootCoordinator: Firebase initialized');

      // Warm up Cloud Functions after Firebase + App Check are ready
      const [{ SearchService }, { warmUpMovieFunctions }] = await Promise.all([
        import('./searchService'),
        import('./tmdbFunctionsApi'),
      ]);
      SearchService.warmUp();
      warmUpMovieFunctions();

      // Resolve auth state
      const user = await Promise.race([
        nativeFirebase.getCurrentUser(),
        this.delay(BOOT_TIMEOUTS.AUTH_RESOLVE_TIMEOUT).then(() => {
          console.warn('BootCoordinator: Auth resolution timeout, proceeding with null user');
          return null;
        }),
      ]);

      console.log('BootCoordinator: Auth resolved, user:', user?.email ?? 'null');

      this.updateState({
        firebaseReady: true,
        authResolved: true,
        user,
      });

    } catch (error) {
      console.error('BootCoordinator: Firebase initialization failed:', error);
      throw error;
    }
  }

  /**
   * Hide the native splash screen
   */
  private async hideSplash(): Promise<void> {
    if (this.isNative) {
      try {
        await SplashScreen.hide({ fadeOutDuration: 300 });
        console.log('BootCoordinator: Native splash hidden');
      } catch (error) {
        console.warn('BootCoordinator: Error hiding splash:', error);
      }
    }
  }

  /**
   * Retry boot after failure
   */
  async retry(): Promise<void> {
    console.log('BootCoordinator: Retrying boot...');

    // Reset state
    this.state = {
      phase: 'boot-screen', // Skip splash on retry
      firebaseReady: false,
      authResolved: false,
      user: null,
      error: null,
      bootStartTime: Date.now(),
      bootDuration: null,
    };
    this.notifyListeners();

    // Clear promise to allow re-boot
    this.bootPromise = null;

    // Retry
    await this.boot();
  }

  /**
   * Utility: Promise-based delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if running on native platform
   */
  isPlatformNative(): boolean {
    return this.isNative;
  }

  /**
   * Get boot timing analytics
   */
  getBootAnalytics(): {
    platform: string;
    bootDuration: number | null;
    phase: BootPhase;
    hadError: boolean;
  } {
    return {
      platform: Capacitor.getPlatform(),
      bootDuration: this.state.bootDuration,
      phase: this.state.phase,
      hadError: this.state.error !== null,
    };
  }
}

// ============================================
// SINGLETON EXPORT
// ============================================

export const bootCoordinator = new BootCoordinator();

// Export class for testing
export { BootCoordinator };

// ============================================
// FIRST POSTER PRELOAD COORDINATION
// ============================================

// BootScreen sets this at module init (before bootCoordinator.boot() hides the splash)
// so bootCoordinator can preload exactly the first poster that will be shown.
export let bootPosterPreloadUrl: string | null = null;
export function setBootPosterPreloadUrl(url: string): void {
  bootPosterPreloadUrl = url;
}
